// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — File System Access handle persistence.
//
// Goal: the SPA at tether.dev remembers which folder on disk the user
// granted as their vault, *across Chrome restarts*. The handle itself
// (`FileSystemDirectoryHandle`) is structured-cloneable since Chrome 86, so
// we stash it directly in IDB. The catch is that **structured-clone preserves
// the handle but NOT its permission grant** — on every fresh tab we have to
// call `requestPermission()` and (per spec) only get a "granted" answer when
// the call originates from a user gesture. See the docstring on
// `getVaultDir()` for the full gotcha list.
//
// We use a tiny dedicated IDB store rather than Dexie because Dexie wants
// to wrap values via structured clone in ways that have historically tripped
// FSAccess handles in some Chrome builds. A plain `IDBObjectStore.put()` is
// the documented happy path.

const FS_DB_NAME = 'tether-brain-v2-fsacc';
const FS_DB_VERSION = 1;
const FS_STORE = 'handles';

/**
 * Legacy single-vault key. Kept stable so v1 users who upgrade to multi-
 * workspace don't lose their granted folder. Workspaces.ts migrates this
 * row to `handle::default` on first multi-workspace boot.
 */
const VAULT_KEY = 'vault-root';
export const LEGACY_VAULT_KEY = VAULT_KEY;

/**
 * Multi-workspace mode — each workspace owns a separate handle. We key under
 * `handle::<workspaceId>` so a single IDB store backs the whole pool. Per-
 * workspace handles let the user point "personal" at `~/personal-notes` and
 * "work" at `~/work-notes` simultaneously without clobbering either grant.
 */
function workspaceHandleKey(workspaceId: string): string {
  return `handle::${workspaceId}`;
}

// ─── minimal FSAccess type surface ──────────────────────────────────────────
//
// `lib.dom.d.ts` shipped these types in recent TypeScript releases but they
// are still missing under some `lib` configurations. We declare the methods
// we touch so the package typechecks regardless of the consumer's lib set.

type FsPermissionMode = 'read' | 'readwrite';

interface FsPermissionDescriptor {
  mode?: FsPermissionMode;
}

interface FsHandle {
  readonly kind: 'file' | 'directory';
  readonly name: string;
  queryPermission?: (desc?: FsPermissionDescriptor) => Promise<PermissionState>;
  requestPermission?: (desc?: FsPermissionDescriptor) => Promise<PermissionState>;
}

export interface VaultDirHandle extends FsHandle {
  readonly kind: 'directory';
  values: () => AsyncIterableIterator<FsHandle>;
  getFileHandle?: (name: string, opts?: { create?: boolean }) => Promise<FsHandle>;
  getDirectoryHandle?: (name: string, opts?: { create?: boolean }) => Promise<VaultDirHandle>;
}

interface ShowDirectoryPickerOptions {
  mode?: FsPermissionMode;
  startIn?: 'documents' | 'desktop' | 'downloads' | 'music' | 'pictures' | 'videos';
  id?: string;
}

interface FsCapableWindow extends Window {
  showDirectoryPicker?: (opts?: ShowDirectoryPickerOptions) => Promise<VaultDirHandle>;
}

// ─── tiny IDB wrapper ───────────────────────────────────────────────────────

function openFsDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(FS_DB_NAME, FS_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(FS_STORE)) {
        db.createObjectStore(FS_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openFsDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(FS_STORE, mode);
        const store = t.objectStore(FS_STORE);
        const req = fn(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        t.oncomplete = () => db.close();
        t.onerror = () => {
          db.close();
          reject(t.error);
        };
      }),
  );
}

async function idbGet(key: string): Promise<unknown> {
  return tx('readonly', (s) => s.get(key));
}

async function idbPut(key: string, value: unknown): Promise<void> {
  await tx('readwrite', (s) => s.put(value, key));
}

async function idbDelete(key: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(key));
}

// ─── public API ─────────────────────────────────────────────────────────────

export interface RequestVaultDirOptions {
  mode?: FsPermissionMode;
  startIn?: ShowDirectoryPickerOptions['startIn'];
}

/**
 * Prompt the user to pick a folder. MUST be called from inside a user gesture
 * (click/keydown handler) — the directory picker won't show up otherwise.
 *
 * Persists the resulting handle in IDB so subsequent sessions can re-grant
 * without showing the picker again.
 */
export async function requestVaultDir(
  opts: RequestVaultDirOptions = {},
): Promise<VaultDirHandle> {
  const w = globalThis.window as FsCapableWindow | undefined;
  if (!w?.showDirectoryPicker) {
    throw new Error(
      'File System Access API not available — Chrome/Edge 86+ required.',
    );
  }
  const handle = await w.showDirectoryPicker({
    mode: opts.mode ?? 'readwrite',
    startIn: opts.startIn ?? 'documents',
    id: 'tether-vault',
  });
  await idbPut(VAULT_KEY, handle);
  return handle;
}

/**
 * Returns the persisted vault directory handle, after re-checking that the
 * user still grants the chosen permission mode.
 *
 * **Gotchas worth knowing:**
 * 1. The handle survives restart — `IDBObjectStore.put()` of a
 *    `FileSystemDirectoryHandle` is supported by Chrome's structured-clone
 *    impl since 86. **But the permission grant does not survive.**
 * 2. On the very first call after a restart, `queryPermission()` returns
 *    `'prompt'`. Calling `requestPermission()` then *requires a user gesture*
 *    or the call resolves to `'denied'` without a UI. This function therefore
 *    optimistically queries first and returns `null` if the user must
 *    re-grant; the caller should put a "Reconnect vault" button on a click
 *    handler and call `requestVaultDir()` (or this function with
 *    `forcePrompt: true`) from inside it.
 * 3. The browser may revoke the persisted handle entirely (e.g. site data
 *    cleared, disk path removed). In that case we delete our IDB entry and
 *    return `null` so the caller falls back to the picker flow.
 */
export async function getVaultDir(opts: {
  mode?: FsPermissionMode;
  /** Skip the permission check and just return the persisted handle. */
  rawOnly?: boolean;
} = {}): Promise<VaultDirHandle | null> {
  const raw = (await idbGet(VAULT_KEY)) as VaultDirHandle | undefined;
  if (!raw) return null;
  if (opts.rawOnly) return raw;

  const mode: FsPermissionMode = opts.mode ?? 'readwrite';
  if (typeof raw.queryPermission !== 'function') {
    // Browser stripped or never had the permission API — return as-is and let
    // the caller's first read/write surface the failure.
    return raw;
  }
  let state: PermissionState;
  try {
    state = await raw.queryPermission({ mode });
  } catch {
    // Handle has been invalidated (the directory was deleted, etc.).
    await idbDelete(VAULT_KEY);
    return null;
  }
  if (state === 'granted') return raw;
  // 'prompt' or 'denied' — caller must re-grant from a user gesture.
  return null;
}

/**
 * Re-prompt for permission on the persisted handle. Must be called from a
 * user gesture (click handler). Use this when `getVaultDir()` returns `null`
 * but you'd like to avoid showing the directory picker again.
 *
 * Returns the handle on success, or `null` if the user denied / no persisted
 * handle exists.
 */
export async function reconnectVaultDir(opts: {
  mode?: FsPermissionMode;
} = {}): Promise<VaultDirHandle | null> {
  const raw = (await idbGet(VAULT_KEY)) as VaultDirHandle | undefined;
  if (!raw) return null;
  const mode: FsPermissionMode = opts.mode ?? 'readwrite';
  if (typeof raw.requestPermission !== 'function') return raw;
  try {
    const state = await raw.requestPermission({ mode });
    return state === 'granted' ? raw : null;
  } catch {
    await idbDelete(VAULT_KEY);
    return null;
  }
}

/** Forget the persisted vault handle. The next call to `getVaultDir()` will
 *  return `null` and the next `requestVaultDir()` will re-show the picker. */
export async function revokeVaultDir(): Promise<void> {
  await idbDelete(VAULT_KEY);
}

/** Probe the environment — `true` iff `showDirectoryPicker` exists. Cheap
 *  enough to call from render. */
export function fsAccessSupported(): boolean {
  const w = globalThis.window as FsCapableWindow | undefined;
  return typeof w?.showDirectoryPicker === 'function';
}

// ─── multi-workspace keyed API ──────────────────────────────────────────────
//
// Design choice: we kept the legacy `requestVaultDir`/`getVaultDir`/
// `reconnectVaultDir`/`revokeVaultDir` exports with their original
// single-handle semantics (they read/write `vault-root`). The keyed API
// below operates on `handle::<workspaceId>` rows in the same IDB store —
// so a single FSAccess DB backs both the legacy default and the multi-
// workspace pool. `workspaces.ts` migrates `vault-root` → `handle::default`
// on first boot, keeping legacy users whole.

/**
 * Persist a directory handle for a specific workspace id. Used by
 * `WorkspacesManager.create()` after the user picks a folder.
 */
export async function putHandleForWorkspace(
  workspaceId: string,
  handle: VaultDirHandle,
): Promise<void> {
  await idbPut(workspaceHandleKey(workspaceId), handle);
}

/**
 * Read the persisted handle for a specific workspace, re-checking permission
 * the same way `getVaultDir()` does for the legacy single-vault case.
 */
export async function getHandleForWorkspace(
  workspaceId: string,
  opts: {
    mode?: FsPermissionMode;
    rawOnly?: boolean;
  } = {},
): Promise<VaultDirHandle | null> {
  const raw = (await idbGet(workspaceHandleKey(workspaceId))) as
    | VaultDirHandle
    | undefined;
  if (!raw) return null;
  if (opts.rawOnly) return raw;

  const mode: FsPermissionMode = opts.mode ?? 'readwrite';
  if (typeof raw.queryPermission !== 'function') return raw;
  let state: PermissionState;
  try {
    state = await raw.queryPermission({ mode });
  } catch {
    await idbDelete(workspaceHandleKey(workspaceId));
    return null;
  }
  if (state === 'granted') return raw;
  return null;
}

/**
 * Re-prompt for permission on the handle saved under `workspaceId`. Must be
 * called from a user gesture. Returns the handle on grant, `null` on deny.
 */
export async function reconnectHandleForWorkspace(
  workspaceId: string,
  opts: { mode?: FsPermissionMode } = {},
): Promise<VaultDirHandle | null> {
  const raw = (await idbGet(workspaceHandleKey(workspaceId))) as
    | VaultDirHandle
    | undefined;
  if (!raw) return null;
  const mode: FsPermissionMode = opts.mode ?? 'readwrite';
  if (typeof raw.requestPermission !== 'function') return raw;
  try {
    const state = await raw.requestPermission({ mode });
    return state === 'granted' ? raw : null;
  } catch {
    await idbDelete(workspaceHandleKey(workspaceId));
    return null;
  }
}

/** Forget the handle bound to `workspaceId`. */
export async function deleteHandleForWorkspace(
  workspaceId: string,
): Promise<void> {
  await idbDelete(workspaceHandleKey(workspaceId));
}

/**
 * Raw legacy handle accessor — used ONLY by the workspaces.ts migration
 * helper. Avoids the permission probe so we can detect "the user picked a
 * folder once" even when the grant has expired.
 */
export async function getRawLegacyHandle(): Promise<VaultDirHandle | null> {
  const raw = (await idbGet(VAULT_KEY)) as VaultDirHandle | undefined;
  return raw ?? null;
}
